import { configureRequestParams, getErrorMessage } from '@/utils/helpers'

import axios from './instance'

/**
 * Gets the list of entity roles, expanding user objects
 *
 * @param {string} entityId - The entity ID
 * @param {string} role - The optional role to filter by
 * @returns {Promise<object>} The entity roles for the specified entity
 */
export const getEntityRoles = async (payload) => {
  const { entityId, sorting, pagination, searchTerm, filters } = payload
  const params = configureRequestParams(sorting, pagination, searchTerm, filters)
  try {
    return await axios.get(`/entities/${entityId}/roles/${params}&expand=user,jobs`)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Gets the profile payload for a single entity role (general info, instructor
 * data, student data) in one call.
 *
 * @param {object} payload
 * @param {string} payload.entityId - The entity ID
 * @param {string} payload.roleId - The entity role ID
 * @returns {Promise<object>} The entity role profile
 */
export const getEntityRoleProfile = async ({ entityId, roleId }) => {
  try {
    return await axios.get(`/entities/${entityId}/roles/${roleId}/profile/`)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Add a new entity role.
 * @param {object} params - The entity role data to be added.
 * @param {string} params.entityId - The entity ID
 * @param {string} params.roles - The role to assign to the user
 * @param {string} params.jobs - The jobs to assign to the user
 * @param {object} params.newUserData - The entity role data to be added.
 * @returns {Promise<object>} The added entity role
 */
export const addEntityRole = async ({ entityId, roles, jobs, ...newUserData }) => {
  try {
    return await axios.post(`/entities/${entityId}/roles/?expand=user,jobs`, {
      roles,
      jobs,
      newUserData,
    })
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Update an entity role.
 *
 * @param {object} payload - The entity role data to be updated.
 * @param {string} payload.entityId - The entity ID
 * @param {string} payload.id - The entity role ID to be updated
 * @returns {Promise<object>} The updated entity role
 */
export const updateEntityRole = async (payload) => {
  try {
    return await axios.patch(`/entities/${payload.entityId}/roles/${payload.id}/`, payload)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Reinvite a user at the entity role level.
 *
 * @param {object} payload - The entity role data to reinvite.
 * @param {string} payload.entityId - The entity ID
 * @param {string} payload.id - The entity role ID to reinvite
 * @returns {Promise<object>} The reinvited entity role
 */
export const reinviteEntityRole = async (payload) => {
  try {
    return await axios.patch(`/entities/${payload.entityId}/roles/${payload.id}/`, {
      reinviteUser: true,
    })
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Set password for a user via their entity role.
 *
 * @param {object} params - The request parameters
 * @param {string} params.entityId - The entity ID
 * @param {string} params.roleId - The entity role ID
 * @param {string} params.newPassword - The new password
 * @returns {Promise<object>} Success message
 */
export const entityRoleSetPassword = async ({ entityId, roleId, newPassword }) => {
  try {
    return await axios.post(`/entities/${entityId}/roles/${roleId}/set-password/`, {
      new_password: newPassword,
    })
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Deletes an entity role.
 *
 * @param {object} payload - The entity role data to be deleted.
 * @param {string} payload.entityId - The entity ID
 * @param {string} payload.id - The entity role ID to be deleted
 * @returns {Promise<object>} 204 No Content if successful
 */
export const deleteEntityRole = async (payload) => {
  try {
    return await axios.delete(`/entities/${payload.entityId}/roles/${payload.id}/`)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Gets the list of jobs for an entity
 *
 * @param {object} payload - The request parameters
 * @param {string} payload.entityId - The entity ID
 * @param {Array} payload.sorting - Sorting configuration
 * @param {object} payload.pagination - Pagination configuration
 * @param {string} payload.searchTerm - Search term
 * @returns {Promise<object>} The jobs for the specified entity
 */
export const getEntityJobs = async (payload) => {
  const { entityId, sorting, pagination, searchTerm } = payload
  const params = configureRequestParams(sorting, pagination, searchTerm)
  try {
    return await axios.get(`/entities/${entityId}/jobs/${params}`)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Add a new job.
 *
 * @param {object} payload - The job data to be added
 * @param {string} payload.entityId - The entity ID
 * @param {string} payload.title - The job title
 * @param {string} payload.description - The job description (optional)
 * @returns {Promise<object>} The added job
 */
export const addEntityJob = async ({ entityId, ...data }) => {
  try {
    return await axios.post(`/entities/${entityId}/jobs/`, data)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Update a job.
 *
 * @param {object} payload - The job data to be updated
 * @param {string} payload.entityId - The entity ID
 * @param {string} payload.id - The job ID to be updated
 * @param {string} payload.title - The job title
 * @param {string} payload.description - The job description (optional)
 * @returns {Promise<object>} The updated job
 */
export const updateEntityJob = async ({ entityId, ...data }) => {
  try {
    return await axios.patch(`/entities/${entityId}/jobs/${data.id}/`, data)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Deletes a job.
 *
 * @param {object} payload - The job data to be deleted
 * @param {string} payload.entityId - The entity ID
 * @param {string} payload.id - The job ID to be deleted
 * @returns {Promise<object>} 204 No Content if successful
 */
export const deleteEntityJob = async (payload) => {
  try {
    return await axios.delete(`/entities/${payload.entityId}/jobs/${payload.id}/`)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}
